import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/** Matches --submenu-animation-speed (base.css) for underlay hide window */
const MENU_CARDS_CLOSING_MS = 360;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 * @property {boolean} menuCardsOpen - Whether the product-cards disclosure is open.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /**
   * @type {AbortController | null}
   */
  #menuCardsAbortController = null;

  /**
   * @type {boolean}
   */
  #restoreFocusToTrigger = false;

  /**
   * @type {ReturnType<typeof setTimeout> | null}
   */
  #remeasureTimeout = null;

  /**
   * Clears data-menu-cards-closing after underlay collapse window.
   * @type {ReturnType<typeof setTimeout> | null}
   */
  #menuCardsClosingTimeout = null;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
    this.#initializeMenuCardsInert();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.#cleanupMutationObserver();
    this.#teardownMenuCardsListeners();
    if (this.#remeasureTimeout) {
      clearTimeout(this.#remeasureTimeout);
      this.#remeasureTimeout = null;
    }
    this.#clearMenuCardsClosing();
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
    if (this.#state.menuCardsOpen) {
      this.#syncMenuCardsPanelGeometry();
      this.#measureAndSetSubmenuHeight(findSubmenu(this.#state.activeItem));
    }
  }, 100);

  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
    menuCardsOpen: false,
  };

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  get #menuCardsEnabled() {
    return this.dataset.menuCards === 'true';
  }

  /** @returns {'click' | 'hover'} */
  get #menuCardsOpenMode() {
    return this.dataset.menuCardsOpenMode === 'hover' ? 'hover' : 'click';
  }

  get #isClickOpenMode() {
    return this.#menuCardsEnabled && this.#menuCardsOpenMode === 'click';
  }

  get #isHoverOpenMode() {
    return this.#menuCardsEnabled && this.#menuCardsOpenMode === 'hover';
  }

  /**
   * Toggle the product-cards disclosure (click mode).
   * @param {PointerEvent | MouseEvent | KeyboardEvent} event
   */
  toggleMenuCards = (event) => {
    if (!this.#isClickOpenMode) return;
    if (!(event.target instanceof Element) || !this.headerComponent) return;

    event.preventDefault();
    event.stopPropagation();

    const item = findMenuItem(event.target);
    if (!item) return;

    const listItem = item.closest('[data-menu-cards-item]');
    if (!listItem) return;

    if (this.#state.menuCardsOpen && this.#state.activeItem === item) {
      this.#restoreFocusToTrigger = true;
      this.#closeMenuCards({ restoreFocus: true });
      return;
    }

    if (this.#state.activeItem && this.#state.activeItem !== item) {
      this.#deactivate(this.#state.activeItem, { skipHoverGuard: true });
    }

    this.#openMenuCards(item, { focusFirstCard: true });
  };

  /**
   * @param {HTMLElement} item
   * @param {{ focusFirstCard?: boolean }} [options]
   */
  #openMenuCards(item, options = {}) {
    this.dispatchEvent(new MegaMenuHoverEvent());

    const previouslyActiveItem = this.#state.activeItem;
    if (previouslyActiveItem && previouslyActiveItem !== item) {
      previouslyActiveItem.ariaExpanded = 'false';
      const prevSubmenu = findSubmenu(previouslyActiveItem);
      if (prevSubmenu) {
        delete prevSubmenu.dataset.active;
        if (prevSubmenu.classList.contains('menu-list__submenu--product-cards')) {
          prevSubmenu.inert = true;
        }
      }
    }

    this.#state.activeItem = item;
    this.#state.menuCardsOpen = true;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    const submenu = findSubmenu(item);
    if (submenu) {
      submenu.dataset.active = '';
      submenu.inert = false;
    }

    this.#clearMenuCardsClosing();
    this.headerComponent?.setAttribute('data-menu-cards-open', '');
    this.dataset.menuCardsOpen = 'true';

    this.#syncMenuCardsPanelGeometry();
    this.#measureAndSetSubmenuHeight(submenu);

    if (this.#isClickOpenMode) {
      this.#setupMenuCardsListeners();
    } else {
      // Hover mode: Esc only
      this.#setupMenuCardsListeners({ clickOutside: false, scrollClose: false });
    }

    if (options.focusFirstCard) {
      requestAnimationFrame(() => {
        const firstLink = submenu?.querySelector(
          '.menu-desktop__product-card, .menu-desktop__product-cards-text-link, .menu-desktop__shop-all'
        );
        if (firstLink instanceof HTMLElement) {
          firstLink.focus({ preventScroll: true });
        }
      });
    }
  }

  /**
   * @param {{ restoreFocus?: boolean }} [options]
   */
  #closeMenuCards(options = {}) {
    const item = this.#state.activeItem;
    const restoreFocus = options.restoreFocus ?? this.#restoreFocusToTrigger;
    this.#restoreFocusToTrigger = false;

    // Keep full-bleed underlay suppressed while height collapses (avoids flash)
    if (this.#menuCardsClosingTimeout) {
      clearTimeout(this.#menuCardsClosingTimeout);
      this.#menuCardsClosingTimeout = null;
    }
    this.headerComponent?.setAttribute('data-menu-cards-closing', '');
    this.headerComponent?.removeAttribute('data-menu-cards-open');
    delete this.dataset.menuCardsOpen;
    this.#teardownMenuCardsListeners();

    if (this.#remeasureTimeout) {
      clearTimeout(this.#remeasureTimeout);
      this.#remeasureTimeout = null;
    }

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');

    this.#menuCardsClosingTimeout = setTimeout(() => {
      this.#menuCardsClosingTimeout = null;
      this.headerComponent?.removeAttribute('data-menu-cards-closing');
    }, MENU_CARDS_CLOSING_MS);

    const submenu = findSubmenu(item);
    this.#state.activeItem = null;
    this.#state.menuCardsOpen = false;
    this.ariaExpanded = 'false';
    if (item) {
      item.ariaExpanded = 'false';
    }
    if (submenu) {
      delete submenu.dataset.active;
      submenu.inert = true;
    }

    if (restoreFocus && item instanceof HTMLElement) {
      item.focus({ preventScroll: true });
    }
  }

  #clearMenuCardsClosing() {
    if (this.#menuCardsClosingTimeout) {
      clearTimeout(this.#menuCardsClosingTimeout);
      this.#menuCardsClosingTimeout = null;
    }
    this.headerComponent?.removeAttribute('data-menu-cards-closing');
  }

  /**
   * @param {{ clickOutside?: boolean, scrollClose?: boolean }} [options]
   */
  #setupMenuCardsListeners(options = {}) {
    const clickOutside = options.clickOutside ?? true;
    const scrollClose = options.scrollClose ?? true;

    this.#teardownMenuCardsListeners();
    this.#menuCardsAbortController = new AbortController();
    const { signal } = this.#menuCardsAbortController;

    document.addEventListener('keydown', this.#onDocumentKeydown, { signal });
    if (clickOutside) {
      document.addEventListener('click', this.#onDocumentClick, { signal, capture: true });
    }
    if (scrollClose) {
      window.addEventListener('scroll', this.#onWindowScroll, { signal, passive: true, capture: true });
    }
  }

  #teardownMenuCardsListeners() {
    this.#menuCardsAbortController?.abort();
    this.#menuCardsAbortController = null;
  }

  /**
   * @param {MouseEvent} event
   */
  #onDocumentClick = (event) => {
    if (!this.#state.menuCardsOpen || !this.#isClickOpenMode) return;
    if (!(event.target instanceof Node)) return;

    const columns = this.headerComponent?.querySelector('.header__columns');
    if (columns?.contains(event.target)) {
      const otherTrigger = /** @type {Element | null} */ (
        event.target instanceof Element
          ? event.target.closest(
              '.menu-list__link:not(.menu-list__link--disclosure), .header-actions__action, a.header-logo'
            )
          : null
      );
      if (otherTrigger && !otherTrigger.closest('[data-menu-cards-item]')) {
        this.#closeMenuCards({ restoreFocus: false });
      }
      return;
    }

    this.#closeMenuCards({ restoreFocus: false });
  };

  /**
   * @param {KeyboardEvent} event
   */
  #onDocumentKeydown = (event) => {
    if (!this.#state.menuCardsOpen) return;
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.#restoreFocusToTrigger = true;
    this.#closeMenuCards({ restoreFocus: true });
  };

  #onWindowScroll = () => {
    if (!this.#state.menuCardsOpen || !this.#isClickOpenMode) return;
    this.#closeMenuCards({ restoreFocus: false });
  };

  #initializeMenuCardsInert() {
    this.querySelectorAll('.menu-list__submenu--product-cards').forEach((submenu) => {
      if (submenu instanceof HTMLElement) {
        submenu.inert = true;
      }
    });
  }

  /**
   * Product-cards panel is positioned against `.header__columns` (left:0; width:100%; top:100%).
   * Kept as a hook for resize remeasure callers — no horizontal CSS vars needed.
   */
  #syncMenuCardsPanelGeometry() {
    // no-op: panel fills the floating columns containing block
  }

  /**
   * @param {HTMLElement | null | undefined} submenu
   * @returns {number}
   */
  #getSubmenuContentHeight(submenu) {
    if (!submenu) return 0;
    return Math.max(submenu.scrollHeight, submenu.offsetHeight, 0);
  }

  /**
   * @param {HTMLElement | null | undefined} submenu
   */
  #measureAndSetSubmenuHeight(submenu) {
    if (!this.headerComponent) return;

    const applyHeight = () => {
      const finalHeight = this.#getSubmenuContentHeight(submenu);
      this.headerComponent?.style.setProperty('--submenu-height', `${finalHeight}px`);
      this.#setFullOpenHeaderHeight(finalHeight);
      this.style.setProperty('--submenu-opacity', '1');
    };

    applyHeight();

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.#syncMenuCardsPanelGeometry();
        applyHeight();
      });
    });

    if (this.#remeasureTimeout) {
      clearTimeout(this.#remeasureTimeout);
    }
    this.#remeasureTimeout = setTimeout(() => {
      this.#syncMenuCardsPanelGeometry();
      applyHeight();
      this.#remeasureTimeout = null;
    }, 120);

    if (submenu) {
      this.#cleanupMutationObserver();
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            applyHeight();
            this.#cleanupMutationObserver();
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });
      setTimeout(() => this.#cleanupMutationObserver(), 500);

      submenu.querySelectorAll('img').forEach((img) => {
        if (img.complete) return;
        img.addEventListener(
          'load',
          () => {
            this.#syncMenuCardsPanelGeometry();
            applyHeight();
          },
          { once: true }
        );
      });
    }
  }

  /**
   * Activate the selected menu item immediately (hover mega menu path / hover cards).
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    if (!(event.target instanceof Element) || !this.headerComponent) return;

    const cardsItem = event.target.closest('[data-menu-cards-item]');

    // Click mode: ignore hover/focus on cards item; don't close cards on sibling hover
    if (this.#isClickOpenMode) {
      if (cardsItem) return;
      if (this.#state.menuCardsOpen) return;
    }

    // Hover mode: open product cards via shared helper
    if (this.#isHoverOpenMode && cardsItem) {
      const item = findMenuItem(event.target);
      if (!item || item == this.#state.activeItem) return;
      this.#openMenuCards(item, { focusFirstCard: false });
      return;
    }

    // Click-mode cards open: sibling hover must not steal
    if (this.#state.menuCardsOpen && this.#isClickOpenMode) return;

    // Closing cards when switching to a non-cards hover item (hover mode)
    if (this.#state.menuCardsOpen) {
      this.#closeMenuCards({ restoreFocus: false });
    }

    this.dispatchEvent(new MegaMenuHoverEvent());

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      submenu.dataset.active = '';

      this.#cleanupMutationObserver();

      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (submenu && submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });

      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    let finalHeight = submenu?.offsetHeight || 0;

    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      finalHeight = 0;
    }

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
    this.style.setProperty('--submenu-opacity', '1');
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    // Click mode: pointerleave on siblings must not close cards
    if (this.#state.menuCardsOpen && this.#isClickOpenMode) return;

    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    // Stay open when moving from trigger into the product-cards submenu
    if (
      this.#state.menuCardsOpen &&
      event.relatedTarget instanceof Node &&
      menu?.contains(event.relatedTarget)
    ) {
      return;
    }

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) return;

    this.#deactivate();
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   * @param {{ skipHoverGuard?: boolean }} [options]
   */
  #deactivate = (item = this.#state.activeItem, options = {}) => {
    if (!item || item != this.#state.activeItem) return;

    if (this.#state.menuCardsOpen) {
      // Click mode: only explicit close paths should dismiss
      if (this.#isClickOpenMode && !options.skipHoverGuard) return;
      this.#closeMenuCards({ restoreFocus: false });
      return;
    }

    if (!options.skipHoverGuard) {
      if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;
    }

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    };

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  if (element.matches('[ref="menuitem"]')) {
    return /** @type {HTMLElement} */ (element);
  }

  return /** @type {HTMLElement | null} */ (
    element?.closest('[ref="menuitem"]') ?? element?.querySelector('[ref="menuitem"]')
  );
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
